import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { ipService } from '../services/ipService';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'technical';
  avatar?: string;
  subscription_status?: 'active' | 'inactive' | 'expired' | 'trial';
  subscription_type?: string;
  is_premium?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  
  setAuth: (user: User, token: string, refreshToken: string, sessionId: string) => void;
  clearAuth: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  checkAuth: () => boolean;
  getToken: () => string | null;
  refreshAccessToken: () => Promise<void>;
}

// ✅ MUTEX para refresh — fuera del store como variable de módulo
let _isRefreshing = false;
let _refreshPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,

      setAuth: (user, token, refreshToken, sessionId) => {
        console.log('🔐 [AUTH-STORE] setAuth llamado:', {
          userId: user.id,
          userEmail: user.email,
          tokenLength: token?.length,
          hasRefreshToken: !!refreshToken,
          hasSessionId: !!sessionId,
        });

        // ✅ Guardar en localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('kikai_token', token);
        localStorage.setItem('refreshToken', refreshToken || '');
        localStorage.setItem('sessionId', sessionId || '');
        localStorage.setItem('user', JSON.stringify(user));

        // Actualizar store
        set({
          user,
          token,
          refreshToken,
          sessionId,
          isAuthenticated: true,
        });

        console.log('✅ [AUTH-STORE] Sesión establecida');
      },

      // =============================================
      // 🚪 CLEAR AUTH — LIMPIA PRIMERO, LUEGO API
      // =============================================
      clearAuth: async () => {
        console.log('🚪 [AUTH-STORE] Limpiando autenticación');
        
        // ✅ CRÍTICO: Capturar sessionId ANTES de limpiar
        const sessionId = localStorage.getItem('sessionId');

        // ✅ PASO 1: Limpiar localStorage SINCRÓNICAMENTE (antes de cualquier navegación)
        localStorage.removeItem('token');
        localStorage.removeItem('kikai_token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        localStorage.removeItem('kikai-auth-storage'); // ✅ Zustand persist key

        // ✅ PASO 2: Limpiar store Zustand
        set({
          user: null,
          token: null,
          refreshToken: null,
          sessionId: null,
          isAuthenticated: false,
        });

        // ✅ PASO 3: Notificar al backend (no bloqueante, best-effort)
        if (sessionId) {
          try {
            const publicIp = await ipService.getPublicIP().catch(() => 'Unknown');
            
            await axios.post('/api/auth/logout', { 
              sessionId,
              publicIp
            }).catch((err) => {
              console.warn('⚠️ [AUTH-STORE] Logout API falló (sesión limpia localmente):', err?.message);
            });
            
            console.log('✅ [AUTH-STORE] Logout notificado al servidor');
          } catch (error) {
            console.error('❌ [AUTH-STORE] Error en logout API:', error);
            // No importa — localStorage ya está limpio
          }
        }
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (!currentUser) return;

        const updatedUser = { ...currentUser, ...userData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        set({ user: updatedUser });
      },

      checkAuth: () => {
        const state = get();
        
        if (state.isAuthenticated && state.user && state.token) {
          // ✅ VERIFICAR EXPIRACIÓN DEL TOKEN
          try {
            const payload = JSON.parse(atob(state.token.split('.')[1]));
            const now = Date.now() / 1000;

            if (payload.exp && payload.exp > now) {
              return true;
            } else {
              console.warn('⏰ [AUTH-STORE] Token expirado en checkAuth');
              // ✅ Limpiar sincrónicamente sin await
              localStorage.removeItem('token');
              localStorage.removeItem('kikai_token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('sessionId');
              localStorage.removeItem('user');
              localStorage.removeItem('kikai-auth-storage');
              
              set({
                user: null,
                token: null,
                refreshToken: null,
                sessionId: null,
                isAuthenticated: false,
              });
              return false;
            }
          } catch (error) {
            console.error('[AUTH-STORE] Error decodificando token:', error);
            set({ isAuthenticated: false });
            return false;
          }
        }

        // Intentar restaurar desde localStorage
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');

        if (token && userStr) {
          try {
            const user = JSON.parse(userStr);
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Date.now() / 1000;

            if (payload.exp && payload.exp > now) {
              set({
                user,
                token,
                refreshToken: localStorage.getItem('refreshToken'),
                sessionId: localStorage.getItem('sessionId'),
                isAuthenticated: true,
              });
              return true;
            }
          } catch (error) {
            console.error('[AUTH-STORE] Error restaurando sesión:', error);
          }
        }

        // Token expirado o inexistente — limpiar todo
        localStorage.removeItem('token');
        localStorage.removeItem('kikai_token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        localStorage.removeItem('kikai-auth-storage');
        
        set({
          user: null,
          token: null,
          refreshToken: null,
          sessionId: null,
          isAuthenticated: false,
        });
        return false;
      },

      getToken: () => {
        const storeToken = get().token;
        if (storeToken) return storeToken;
        return localStorage.getItem('token');
      },

      // =============================================
      // 🔄 REFRESH CON MUTEX — evita llamadas concurrentes
      // =============================================
      refreshAccessToken: async () => {
        // ✅ Si ya hay un refresh en curso, esperar ese mismo promise
        if (_isRefreshing && _refreshPromise) {
          return _refreshPromise;
        }

        const { sessionId, refreshToken } = get();

        if (!sessionId || !refreshToken) {
          throw new Error('No hay sesión activa para refresh');
        }

        _isRefreshing = true;
        _refreshPromise = (async () => {
          try {
            const response = await axios.post('/auth/refresh', {
              sessionId,
              refreshToken,
            });

            const { accessToken } = response.data;
            
            // ✅ Actualizar TODOS los tokens consistentemente
            localStorage.setItem('token', accessToken);
            localStorage.setItem('kikai_token', accessToken);
            set({ token: accessToken });

            console.log('✅ [AUTH-STORE] Token renovado');
          } catch (error: any) {
            console.error('[AUTH-STORE] Error en refresh:', error?.message);

            const code = error.response?.data?.code;
            if (code === 'INVALID_SESSION') {
              alert('Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo');
              await get().clearAuth();
              window.location.href = '/login?reason=invalid_session';
            } else if (code === 'SESSION_TIMEOUT') {
              alert('Tu sesión expiró por inactividad');
              await get().clearAuth();
              window.location.href = '/login?reason=session_timeout';
            }

            throw error;
          } finally {
            _isRefreshing = false;
            _refreshPromise = null;
          }
        })();

        return _refreshPromise;
      },
    }),
    {
      name: 'kikai-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        sessionId: state.sessionId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export const getCurrentUser = () => useAuthStore.getState().user;
export const isAuthenticated = () => useAuthStore.getState().checkAuth();
export default useAuthStore;